import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient, { toasterFromError, formatDate } from '../../apiClient';

const LEVELS = ['ALL', 'INFO', 'SUCCESS', 'WARNING', 'ERROR', 'DEBUG'];
const METHODS = ['ALL', 'GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function rowKey(r, i) { return r.id || r.request_id || r.ota_id || (r.timestamp + '_' + i); }

const LogsTab = () => {
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialError, setInitialError] = useState(null);
  const [logs, setLogs] = useState([]);
  const [maxStored] = useState(1500);
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [level, setLevel] = useState('ALL');
  const [method, setMethod] = useState('ALL');
  const [appName, setAppName] = useState('ALL');
  const [apps, setApps] = useState([]);
  const [streamConnected, setStreamConnected] = useState(false);
  const [streamFailed, setStreamFailed] = useState(false);
  const viewerRef = useRef(null);
  const eventSourceRef = useRef(null);
  const pollingTimerRef = useRef(null);
  const lastSeenRef = useRef(null);
  const pausedBufRef = useRef([]);
  const [, forceTick] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get('/api/applications', { params: { limit: 1000 } });
        setApps((r.data && r.data.applications) || []);
      } catch (_) { /* apps filter just won't be populated */ }
    })();
  }, []);

  const setLastSeenFromRow = useCallback((row) => {
    if (!row || !row.timestamp) return;
    const t = new Date(row.timestamp).getTime();
    if (!lastSeenRef.current || t > lastSeenRef.current) lastSeenRef.current = t;
  }, []);

  useEffect(() => {
    setInitialLoading(true); setInitialError(null);
    (async () => {
      try {
        const r = await apiClient.get('/api/logs', { params: { limit: 200 } });
        const rows = (r.data && r.data.logs) || [];
        const sorted = [...rows].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
        setLogs(sorted);
        sorted.forEach(setLastSeenFromRow);
      } catch (e) {
        setInitialError(toasterFromError(e, 'Unable to load initial logs'));
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [setLastSeenFromRow]);

  const append = useCallback((rows) => {
    if (!rows || rows.length === 0) return;
    const sortedChunk = [...rows].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
    setLogs(prev => {
      const seen = new Set(prev.map(r => rowKey(r, 0)));
      const merged = [...prev];
      for (const r of sortedChunk) {
        const k = rowKey(r, 0);
        if (seen.has(k)) continue;
        merged.push(r);
        seen.add(k);
        setLastSeenFromRow(r);
      }
      merged.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
      if (merged.length > maxStored) return merged.slice(merged.length - maxStored);
      return merged;
    });
    forceTick(n => n + 1);
  }, [maxStored, setLastSeenFromRow]);

  const startPollingFallback = useCallback(() => {
    if (pollingTimerRef.current) return;
    pollingTimerRef.current = setInterval(async () => {
      try {
        const params = { limit: 200 };
        if (lastSeenRef.current) params.since = new Date(lastSeenRef.current).toISOString();
        const r = await apiClient.get('/api/logs', { params });
        const rows = (r.data && r.data.logs) || [];
        append(rows);
      } catch (_) {}
    }, 2000);
  }, [append]);

  const stopPollingFallback = useCallback(() => {
    if (pollingTimerRef.current) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const connectSSE = useCallback(() => {
    try {
      if (eventSourceRef.current) { try { eventSourceRef.current.close(); } catch (_) {} eventSourceRef.current = null; }
      if (typeof window === 'undefined' || !window.EventSource) {
        setStreamConnected(false); setStreamFailed(true); startPollingFallback(); return;
      }
      setStreamFailed(false);
      const es = new EventSource('/api/logs/stream', { withCredentials: false });
      eventSourceRef.current = es;

      es.addEventListener('connected', () => {
        try {
          setStreamConnected(true);
          stopPollingFallback();
        } catch (_) { setStreamConnected(true); }
      });

      es.addEventListener('log', (ev) => {
        try {
          const row = JSON.parse(ev.data);
          if (paused) { pausedBufRef.current.push(row); return; }
          append([row]);
        } catch (_) {}
      });

      es.addEventListener('message', (ev) => {
        if (!ev || !ev.data) return;
        try {
          const row = JSON.parse(ev.data);
          if (row && typeof row === 'object' && (row.timestamp || row.message)) {
            if (paused) { pausedBufRef.current.push(row); return; }
            append([row]);
          }
        } catch (_) {}
      });

      es.addEventListener('error', () => {
        setStreamConnected(false); setStreamFailed(true);
        try { if (es.readyState === EventSource.CLOSED) { startPollingFallback(); } } catch (_) { startPollingFallback(); }
      });

      es.addEventListener('open', () => { setStreamConnected(true); setStreamFailed(false); stopPollingFallback(); });

    } catch (e) {
      setStreamConnected(false); setStreamFailed(true); startPollingFallback();
    }
  }, [append, paused, startPollingFallback, stopPollingFallback]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) { try { eventSourceRef.current.close(); } catch (_) {} eventSourceRef.current = null; }
    stopPollingFallback();
    setStreamConnected(false);
  }, [stopPollingFallback]);

  useEffect(() => {
    connectSSE();
    return () => disconnect();
  }, [connectSSE, disconnect]);

  useEffect(() => {
    if (!paused && pausedBufRef.current.length > 0) {
      const buf = pausedBufRef.current;
      pausedBufRef.current = [];
      append(buf);
    }
  }, [paused, append]);

  useEffect(() => {
    if (autoScroll && viewerRef.current) {
      viewerRef.current.scrollTop = viewerRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const filtered = useMemo(() => {
    return logs.filter(r => {
      if (level !== 'ALL' && r.level !== level) return false;
      if (method !== 'ALL' && r.method !== method) return false;
      if (appName !== 'ALL') {
        const a = r.appName || '';
        if (appName === '(NONE)') { if (a) return false; }
        else if (a !== appName) return false;
      }
      return true;
    });
  }, [logs, level, method, appName]);

  const clearAll = () => { setLogs([]); pausedBufRef.current = []; };

  const togglePause = () => setPaused(p => !p);
  const toggleAutoScroll = () => setAutoScroll(a => !a);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ color: '#ECF0F5', fontSize: 20, fontWeight: 700, margin: 0 }}>
          <i className="fa fa-terminal" style={{ color: '#0DB8DE', marginRight: 8 }} aria-hidden="true"></i>
          Live Backend Logs
        </h2>
        <div style={{ fontSize: 12, color: '#8a9396' }}>
          Showing {filtered.length} / {logs.length} entries (max stored {maxStored})
        </div>
      </div>

      <div className="logs-toolbar">
        <div className="logs-toolbar-group">
          <span className={`live-indicator ${!streamConnected || streamFailed ? 'disconnected' : ''}`} title={streamConnected ? 'Connected via SSE stream' : (streamFailed ? 'Live stream unavailable — polling fallback active' : 'Connecting…')}>
            <span className="live-dot"></span>
            {streamConnected ? 'Live' : (streamFailed ? '● Polling' : 'Connecting…')}
          </span>
          {streamFailed && (
            <button className="btn-toggle on" onClick={connectSSE}>
              <i className="fa fa-plug" style={{ marginRight: 6 }} aria-hidden="true"></i>Reconnect
            </button>
          )}
        </div>
        <div className="logs-toolbar-group">
          <button className={`btn-toggle ${!paused ? 'on' : ''}`} onClick={togglePause}>
            <i className={`fa ${paused ? 'fa-play' : 'fa-pause'}`} style={{ marginRight: 6 }} aria-hidden="true"></i>
            {paused ? 'Resume' : 'Pause'}
          </button>
          <button className="btn-toggle" onClick={clearAll}>
            <i className="fa fa-trash-alt" style={{ marginRight: 6 }} aria-hidden="true"></i>
            Clear
          </button>
          <button className={`btn-toggle ${autoScroll ? 'on' : ''}`} onClick={toggleAutoScroll}>
            <i className="fa fa-arrow-down" style={{ marginRight: 6 }} aria-hidden="true"></i>
            Auto Scroll: {autoScroll ? 'ON' : 'OFF'}
          </button>
        </div>
        <div className="logs-toolbar-group">
          <label htmlFor="f-level" style={{ color: '#8a9396', fontSize: 12, marginRight: 4 }}>Level</label>
          <select id="f-level" className="filter-select" value={level} onChange={(e) => setLevel(e.target.value)}>
            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <label htmlFor="f-app" style={{ color: '#8a9396', fontSize: 12, marginRight: 4 }}>Application</label>
          <select id="f-app" className="filter-select" value={appName} onChange={(e) => setAppName(e.target.value)}>
            <option value="ALL">ALL APPLICATIONS</option>
            <option value="(NONE)">— Unknown / Global —</option>
            {apps.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
          </select>
          <label htmlFor="f-method" style={{ color: '#8a9396', fontSize: 12, marginRight: 4 }}>Method</label>
          <select id="f-method" className="filter-select" value={method} onChange={(e) => setMethod(e.target.value)}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>

      {initialLoading && (
        <div style={{ marginTop: 20 }} className="loading-spinner-wrap">
          <div className="spinner-border text-info" role="status"><span className="visually-hidden">Loading…</span></div>
          <div style={{ marginTop: 12, fontSize: 13 }}>Loading initial logs…</div>
        </div>
      )}

      {!initialLoading && initialError && (
        <div className="error-alert" style={{ marginTop: 20 }}>
          <span>Unable to load initial logs. {initialError}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-toggle on" onClick={() => window.location.reload()}>Retry (reload)</button>
          </div>
        </div>
      )}

      {!initialLoading && (
        filtered.length === 0 ? (
          <div className="empty-state" style={{ marginTop: 20 }}>
            <i className="fa fa-scroll" style={{ fontSize: 32, marginBottom: 14, color: '#0DB8DE' }} aria-hidden="true"></i>
            <div className="empty-state-title">No backend activity yet.</div>
            <div style={{ fontSize: 12 }}>Interact with the application (e.g. reload Applications tab) to generate logs.</div>
          </div>
        ) : (
          <div className="logs-viewer" ref={viewerRef}>
            {filtered.map((r, i) => {
              const t = r.timestamp ? formatDate(r.timestamp).split(' ').slice(-1)[0] : '';
              const lv = r.level || 'INFO';
              return (
                <div className="log-entry" key={rowKey(r, i)}>
                  <span className="log-entry-time">{t}</span>
                  <span className={`level-badge ${lv}`}>{lv}</span>
                  <span className="log-entry-body">
                    <span className="log-entry-tags">
                      {r.method && <span className={`tag method ${r.method}`}>{r.method}</span>}
                      {r.status_code != null && (
                        <span className={`tag method ${r.status_code >= 400 ? 'DELETE' : (r.status_code >= 300 ? 'PUT' : 'GET')}`}
                              style={{ marginLeft: r.method ? 0 : 0 }}>
                          {r.status_code}
                        </span>
                      )}
                      {r.response_time_ms != null && <span className="tag">{r.response_time_ms}ms</span>}
                      {r.appName && <span className="tag">{r.appName}</span>}
                      {r.ota_id && <span className="tag ota">{r.ota_id}</span>}
                    </span>
                    {r.message}
                  </span>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default LogsTab;
